// Cableado de red del puente de descarga de evidencia (ver
// `utils/evidenceDownload.ts` para el porqué y la lógica pura, que es la que
// se probó contra segmentos `.ts` reales).
import { getCurrentFirebaseToken } from './client';
import {
  downloadClip,
  remuxToFragmentedMp4,
  type ClipDownloadDeps,
  type ClipDownloadOptions,
  type ClipDownloadResult,
  type MuxLib,
} from '../../utils/evidenceDownload';

export class ClipHttpError extends Error {
  readonly status: number;
  constructor(status: number, what: string) {
    super(`${what} respondió ${status}`);
    this.name = 'ClipHttpError';
    this.status = status;
  }
}

const deps: ClipDownloadDeps = {
  // La playlist es de ESTA API (`/v1/...`) y se autoriza con el token; el
  // token se pide en cada descarga, no se reutiliza (contrato §7).
  fetchPlaylist: async (url, signal) => {
    const token = await getCurrentFirebaseToken();
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, signal });
    if (!res.ok) throw new ClipHttpError(res.status, 'La playlist');
    return res.text();
  },
  // Los segmentos son URLs firmadas de GCS: NUNCA llevan el Bearer (mismo
  // criterio que `useHlsPlayer`), la firma ya es su autorización.
  fetchSegment: async (url, signal) => {
    const res = await fetch(url, { signal });
    if (!res.ok) throw new ClipHttpError(res.status, 'El segmento');
    return new Uint8Array(await res.arrayBuffer());
  },
  // `mux.js` se carga solo al descargar: no engorda el arranque de la app.
  remux: async segments => {
    const mp4 = (await import('mux.js/cjs/mp4')) as unknown as MuxLib['mp4'] & { default?: MuxLib['mp4'] };
    return remuxToFragmentedMp4({ mp4: mp4.Transmuxer ? mp4 : (mp4.default as MuxLib['mp4']) }, segments);
  },
};

export function downloadEvidenceClip(playlistUrl: string, options?: ClipDownloadOptions): Promise<ClipDownloadResult> {
  return downloadClip(playlistUrl, deps, options);
}

/** Dispara la descarga del navegador. `blob:` + `download` no abre popup, así que Safari no lo bloquea tras un `await`. */
export function saveBlobAsFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revocar de inmediato puede cortar la descarga en algunos navegadores.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
